import React, { useEffect, useState } from 'react';
import { Heart, Repeat2, Award, Quote } from 'lucide-react';
import { formatClientCount } from '../utils/stats';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * TrustStrip — Live social proof pulled from real recurring-client data.
 *
 * Renders nothing if data isn't loaded or no recurring clients exist.
 *
 * Variants:
 *  - "full"    : 3-card grid for homepage (full social proof block)
 *  - "compact" : single-line testimonial-style banner
 */
const formatMonthsLabel = (m) => {
  if (!m || m < 1) return { value: '<1', suffix: 'mois' };
  if (m < 12) return { value: m, suffix: 'mois' };
  const years = Math.floor(m / 12);
  return { value: years, suffix: years > 1 ? 'ans' : 'an' };
};

const TrustStrip = ({ variant = 'full', className = '' }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/public/job-stats`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((json) => {
        if (!cancelled) setData(json);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (!data || !(data.total_clients || data.recurring_clients)) return null;

  const {
    recurring_clients,
    total_clients,
    recurring_jobs_pct,
    longest_relationship_months,
    avg_relationship_months,
  } = data;

  const clientsLabel = formatClientCount(total_clients ?? recurring_clients);
  const longest = formatMonthsLabel(longest_relationship_months);
  const avg = formatMonthsLabel(avg_relationship_months);

  // ----- COMPACT (single-line social proof) -------------------------------
  if (variant === 'compact') {
    return (
      <div
        data-testid="trust-strip-compact"
        className={`inline-flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 ${className}`}
      >
        <Quote size={18} className="text-amber-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <p className="text-sm text-gray-800 leading-snug">
          <strong className="text-amber-900">{clientsLabel} clients</strong> me font confiance,
          saison après saison
          {longest_relationship_months >= 6 && (
            <>
              {' '}— le plus ancien depuis{' '}
              <strong className="text-amber-900">
                {longest.value} {longest.suffix}
              </strong>
            </>
          )}
          .
        </p>
      </div>
    );
  }

  // ----- FULL (3-card homepage block) ------------------------------------
  return (
    <section
      data-testid="trust-strip-full"
      className={`py-12 bg-gradient-to-br from-amber-50/60 via-white to-emerald-50/40 ${className}`}
      aria-labelledby="trust-strip-heading"
    >
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-emerald-700 mb-2">
              <Heart size={14} aria-hidden="true" />
              La fidélité, ma meilleure carte de visite
            </span>
            <h2
              id="trust-strip-heading"
              className="text-2xl lg:text-3xl font-bold text-gray-900"
            >
              Une clientèle qui revient, saison après saison
            </h2>
            <p className="text-gray-600 mt-2 max-w-2xl mx-auto text-sm">
              Mes chiffres parlent d'eux-mêmes : la majorité de mon activité repose sur des contrats
              d'entretien réguliers (tonte mensuelle, taille saisonnière), preuve d'un travail soigné
              et d'une relation de confiance durable.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Card 1 — Loyal clients */}
            <div className="bg-white rounded-2xl border-2 border-emerald-100 p-6 text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Heart size={22} className="text-emerald-700" aria-hidden="true" />
              </div>
              <div className="text-4xl font-extrabold text-emerald-700 tabular-nums">
                {clientsLabel}
              </div>
              <div className="text-sm font-semibold text-gray-900 mt-1">
                clients réguliers
              </div>
              <p className="text-xs text-gray-500 mt-1 leading-snug">
                qui me font confiance, saison après saison
              </p>
            </div>

            {/* Card 2 — % recurring */}
            <div className="bg-white rounded-2xl border-2 border-emerald-100 p-6 text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Repeat2 size={22} className="text-emerald-700" aria-hidden="true" />
              </div>
              <div className="text-4xl font-extrabold text-emerald-700 tabular-nums">
                {recurring_jobs_pct}
                <span className="text-2xl">%</span>
              </div>
              <div className="text-sm font-semibold text-gray-900 mt-1">
                de mon activité
              </div>
              <p className="text-xs text-gray-500 mt-1 leading-snug">
                en clientèle récurrente
              </p>
            </div>

            {/* Card 3 — Longest relationship */}
            <div className="bg-white rounded-2xl border-2 border-emerald-100 p-6 text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Award size={22} className="text-emerald-700" aria-hidden="true" />
              </div>
              <div className="text-4xl font-extrabold text-emerald-700 tabular-nums">
                {longest.value}
                <span className="text-xl">{' '}{longest.suffix}</span>
              </div>
              <div className="text-sm font-semibold text-gray-900 mt-1">
                relation la plus longue
              </div>
              <p className="text-xs text-gray-500 mt-1 leading-snug">
                {avg_relationship_months > 0 && (
                  <>moyenne&nbsp;: {avg.value} {avg.suffix} par client fidèle</>
                )}
              </p>
            </div>
          </div>

          <p className="text-center text-xs text-gray-500 mt-6 italic">
            Données réelles, anonymisées, calculées automatiquement à partir de mon planning.
          </p>
        </div>
      </div>
    </section>
  );
};

export default TrustStrip;
